import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import { TEST_IDS } from "@/constants/testIds";
import { submitInquiry } from "@/lib/api";
import { useT } from "@/i18n/LanguageContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// Canonical English values sent to the backend (email templates depend on them).
const CANONICAL_TYPES = [
  "Diamond Inquiry",
  "Precious Stone Inquiry",
  "Private Consultation",
  "Jewelry / Custom Request",
  "Other",
];

const initial = {
  name: "",
  email: "",
  phone: "",
  inquiry_type: "Diamond Inquiry",
  message: "",
  company: "",
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const InquiryForm = () => {
  const { t } = useT();
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState({ type: "idle", message: "" });

  const localizedTypes = t("inquiry.types");

  useEffect(() => {
    const onPreselect = (e) => {
      const type = e?.detail;
      if (typeof type === "string" && CANONICAL_TYPES.includes(type)) {
        setForm((prev) => ({ ...prev, inquiry_type: type }));
        if (status.type === "error") setStatus({ type: "idle", message: "" });
      }
    };
    window.addEventListener("ck:preselect-inquiry-type", onPreselect);
    return () =>
      window.removeEventListener("ck:preselect-inquiry-type", onPreselect);
  }, [status.type]);

  const setField = (k, v) => {
    setForm((prev) => ({ ...prev, [k]: v }));
    if (status.type === "error") setStatus({ type: "idle", message: "" });
  };

  const validate = () => {
    if (!form.name.trim()) return t("inquiry.errValidName");
    if (!form.email.trim() || !EMAIL_REGEX.test(form.email.trim())) {
      return t("inquiry.errValidEmail");
    }
    if (!form.message.trim()) return t("inquiry.errValidMessage");
    return null;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    const validationError = validate();
    if (validationError) {
      setStatus({ type: "error", message: validationError });
      return;
    }

    setLoading(true);
    setStatus({ type: "idle", message: "" });

    try {
      await submitInquiry({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        inquiry_type: form.inquiry_type,
        message: form.message.trim(),
        company: form.company,
      });
      setForm(initial);
      setStatus({ type: "success", message: t("inquiry.successMsg") });
    } catch (err) {
      const detail = err?.response?.data?.detail || t("inquiry.errGeneric");
      setStatus({
        type: "error",
        message: typeof detail === "string" ? detail : t("inquiry.errGeneric"),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid={TEST_IDS.inquiry.root}
      className="relative py-28 md:py-40 bg-white overflow-hidden"
    >
      <div className="absolute inset-x-0 top-0 luxe-divider" />
      <div className="container-luxe">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5"
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="font-mono text-[11px] tracking-[0.4em] text-gold">
                {t("inquiry.index")}
              </span>
              <div className="h-px w-12 bg-gold" />
              <span className="overline">{t("inquiry.overline")}</span>
            </div>
            <h2 className="h-display text-4xl md:text-5xl lg:text-6xl text-ink text-balance">
              {t("inquiry.title1")}
              <br />
              <span className="italic">{t("inquiry.title2")}</span>
            </h2>
            <p className="mt-8 max-w-md text-base leading-relaxed text-muted_text font-light">
              {t("inquiry.body1")}
            </p>
            <p className="mt-5 max-w-md text-sm leading-relaxed text-muted_text/90 font-light">
              {t("inquiry.body2")}
            </p>
          </motion.div>

          <motion.form
            data-testid={TEST_IDS.inquiry.form}
            onSubmit={onSubmit}
            noValidate
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
            className="lg:col-span-7"
          >
            <div
              aria-hidden="true"
              style={{
                position: "absolute",
                left: "-10000px",
                top: "auto",
                width: "1px",
                height: "1px",
                overflow: "hidden",
              }}
            >
              <label htmlFor="company">Company (leave blank)</label>
              <input
                id="company"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                data-testid={TEST_IDS.inquiry.honeypot}
                value={form.company}
                onChange={(e) => setField("company", e.target.value)}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8">
              <div>
                <label htmlFor="name" className="overline-soft block mb-3">
                  {t("inquiry.labelName")}
                </label>
                <input
                  id="name"
                  type="text"
                  required
                  data-testid={TEST_IDS.inquiry.name}
                  value={form.name}
                  onChange={(e) => setField("name", e.target.value)}
                  className="w-full bg-transparent border-0 border-b border-ink/25 focus:border-ink focus:outline-none focus:ring-0 px-0 py-3 text-base text-ink placeholder:text-ink/30 transition-colors"
                  placeholder={t("inquiry.placeholderName")}
                />
              </div>
              <div>
                <label htmlFor="email" className="overline-soft block mb-3">
                  {t("inquiry.labelEmail")}
                </label>
                <input
                  id="email"
                  type="email"
                  required
                  data-testid={TEST_IDS.inquiry.email}
                  value={form.email}
                  onChange={(e) => setField("email", e.target.value)}
                  className="w-full bg-transparent border-0 border-b border-ink/25 focus:border-ink focus:outline-none focus:ring-0 px-0 py-3 text-base text-ink placeholder:text-ink/30 transition-colors"
                  placeholder={t("inquiry.placeholderEmail")}
                />
              </div>
              <div>
                <label htmlFor="phone" className="overline-soft block mb-3">
                  {t("inquiry.labelPhone")}
                </label>
                <input
                  id="phone"
                  type="text"
                  data-testid={TEST_IDS.inquiry.phone}
                  value={form.phone}
                  onChange={(e) => setField("phone", e.target.value)}
                  className="w-full bg-transparent border-0 border-b border-ink/25 focus:border-ink focus:outline-none focus:ring-0 px-0 py-3 text-base text-ink placeholder:text-ink/30 transition-colors"
                  placeholder={t("inquiry.placeholderPhone")}
                />
              </div>
              <div>
                <label className="overline-soft block mb-3">
                  {t("inquiry.labelType")}
                </label>
                <Select
                  value={form.inquiry_type}
                  onValueChange={(v) => setField("inquiry_type", v)}
                >
                  <SelectTrigger
                    data-testid={TEST_IDS.inquiry.type}
                    className="w-full bg-transparent border-0 border-b border-ink/25 rounded-none px-0 py-3 h-auto text-base text-ink focus:ring-0 focus:border-ink hover:border-ink transition-colors"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CANONICAL_TYPES.map((canonical, i) => (
                      <SelectItem key={canonical} value={canonical}>
                        {localizedTypes[i]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <label htmlFor="message" className="overline-soft block mb-3">
                  {t("inquiry.labelMessage")}
                </label>
                <textarea
                  id="message"
                  required
                  rows={5}
                  data-testid={TEST_IDS.inquiry.message}
                  value={form.message}
                  onChange={(e) => setField("message", e.target.value)}
                  className="w-full bg-transparent border-0 border-b border-ink/25 focus:border-ink focus:outline-none focus:ring-0 px-0 py-3 text-base text-ink placeholder:text-ink/30 resize-none transition-colors"
                  placeholder={t("inquiry.placeholderMessage")}
                />
              </div>
            </div>

            <div className="mt-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
              <p className="text-xs text-muted_text/80 font-light max-w-md leading-relaxed">
                {t("inquiry.privacyNote")}
              </p>
              <button
                type="submit"
                data-testid={TEST_IDS.inquiry.submit}
                disabled={loading}
                aria-busy={loading}
                className="group relative inline-flex items-center gap-3 bg-ink text-white px-9 py-4 font-mono text-[11px] uppercase tracking-[0.32em] hover:bg-graphite disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-500"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    {t("inquiry.sending")}
                  </>
                ) : (
                  <>
                    {t("inquiry.submit")}
                    <span className="inline-block transition-transform duration-500 group-hover:translate-x-1">
                      →
                    </span>
                  </>
                )}
              </button>
            </div>

            {status.type === "success" && (
              <motion.div
                data-testid={TEST_IDS.inquiry.success}
                role="status"
                aria-live="polite"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                className="mt-10 border border-ink/10 bg-white/60 backdrop-blur-sm px-6 py-5 flex items-start gap-5"
              >
                <span className="mt-1 inline-block h-[1px] w-10 bg-gold shrink-0" />
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.32em] text-gold mb-1.5">
                    {t("inquiry.confirmed")}
                  </div>
                  <div className="font-display text-lg md:text-xl text-ink leading-snug">
                    {status.message}
                  </div>
                </div>
              </motion.div>
            )}

            {status.type === "error" && (
              <motion.div
                data-testid={TEST_IDS.inquiry.error}
                role="alert"
                aria-live="assertive"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                className="mt-10 border border-ink/10 bg-white/60 backdrop-blur-sm px-6 py-5 flex items-start gap-5"
              >
                <span className="mt-1 inline-block h-[1px] w-10 bg-ink/60 shrink-0" />
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.32em] text-ink/70 mb-1.5">
                    {t("inquiry.errorTitle")}
                  </div>
                  <p className="text-sm text-ink/85 font-light leading-relaxed max-w-md">
                    {status.message}
                  </p>
                </div>
              </motion.div>
            )}
          </motion.form>
        </div>
      </div>
    </section>
  );
};
